import type {
  AdaptiveLogger,
  CancellationToken,
  CreateNewParams,
  LegacyPerformanceData,
} from "@1js/office-online-jsapi-core";
import type { Health } from "@1js/office-online-qos-small";
import type { InitPackageParams } from "@1js/owl-shared-types";
import type { WiseEnvironment } from "@1js/wise-environments";
import type { WacBootstrapParams } from "../bootstrapper";
import type { PopupBlockedDialogProps } from "../handlers";
import type { InitializeTwoStepCreateNewFn } from "../utility";
import type { AccessTokenResponseFunction } from "./accessToken";
import type {
  CreateDocumentFunction,
  DocumentBootInfo,
  GraphItemFetchFunction,
  WopiHostData,
} from "./bootstrapper";
import type {
  AppInitDiagnostics,
  DocRebootSupportMode,
  MeetingInfo,
  OfficeApplication,
  OfficeApplicationCAAEMessageEventArgs,
  OfficeApplicationEventHandler,
  OfficeApplicationFrameChangedEventArgs,
  OfficeApplicationMoveToFolderEventArgs,
  OfficeApplicationSharedFilePickerEventArgs,
  OfficeApplicationUserAccessEventArgs,
  OfficeDocumentSession,
} from "./jsapi";
import type { OpenShareControlFunction } from "./share";
import type { CustomPerformanceResourceTiming } from "./logger";

export enum DocQueryParams {
  DriveId = "drive",
  ItemId = "item",
  DocUrl = "file",
  ContentUri = "contenturi",
  Nav = "nav",
}

export enum KpiMetricName {
  KPI_DavUrlFromIds = "DavUrlFromIds",
  KPI_FilePicker = "FilePicker",
  KPI_OwlBoot = "OwlBoot",
  KPI_OwlPostWacTokenForTeams = "OwlPostWacTokenForTeams", // Only applies to Teams based Ui Hosts
  KPI_PostWacToken = "PostWacToken",
  KPI_PrebootDocument = "PrebootDocument",
  KPI_PrebootDocumentOpen = "PrebootDocumentOpen",
  KPI_WopiAuthRefresh = "WopiAuthRefresh",
  KPI_WopiAuthFetch = "WopiAuthFetch",
  KPI_WopiInfoFromUrl = "WopiInfoFromUrl",
  KPI_PrintRedirect = "PrintRedirect",
  KPI_PrintInBrowser = "PrintInBrowser",
}

export enum OwningFeatureCrewAlias {
  FC_Owl = "owlfc",
}

export enum QosVetoName {
  QosVeto_ShareDialogExperienceDegraded = "ShareDialogExperienceDegraded",
  QosVeto_CreateNewExperienceDegraded = "CreateNewExperienceDegraded",
  QosVeto_ExternalLinksExperienceDegraded = "ExternalLinksExperienceDegraded",
  QosVeto_WopiAuthExperienceDegraded = "WopiAuthExperienceDegraded",
  QosVeto_FilePickerFailed = "FilePickerFailed",
  QosVeto_OpenInExperienceFailed = "OpenInExperienceFailed",
  QosVeto_PrintRedirectFailed = "PrintRedirectFailed",
  QosVeto_PrintInBrowserFailed = "PrintInBrowserFailed",
}

// Used for the errorName parameter in calls to log small Qos errors (recordQosError).
export enum QosErrorName {
  QosError_VroomWopiFailure = "VroomWopiFailure",
  QosError_OfficeBootstrapperNotLoaded = "OfficeBootstrapperNotLoaded",
  QosError_EncounteredJsApiError = "EncounteredJsApiError",
  QosError_FailedToLoadDocument = "FailedToLoadDocument",
  QosError_AttemptingToBootDocumentFailed = "AttemptingToBootDocumentFailed",
  QosError_DavUrlFromIdsFailure = "DavUrlFromIdsFailure",
  QosError_ShareDialogHandlerInitFailure = "ShareDialogHandlerInitFailure",
  QosError_ShareDialogHandlerHostReportedFailure = "ShareDialogHandlerHostReportedFailure",
  QosError_ShareDialogHandlerGenericFailure = "ShareDialogHandlerGenericFailure",
  QosError_ShareDialogHandlerPerfFailure = "ShareDialogHandlerPerfFailure",
  QosError_CreateNewHandlerRebootSessionFailure = "CreateNewHandlerRebootSessionFailure",
  QosError_CreateNewHandlerCreateDocumentFailure = "CreateNewHandlerCreateDocumentFailure",
  QosError_CreateNewHandlerEndSessionFailure = "CreateNewHandlerEndSessionFailure",
  QosError_CreateNewHandlerGenericFailure = "CreateNewHandlerGenericFailure",
  QosError_WopiRefreshAuthTokenFailure = "WopiRefreshAuthTokenFailure",
  QosError_WopiFetchAuthTokenFailure = "WopiFetchAuthTokenFailure",
  QosError_WopiInfoFromUrlFailure = "WopiInfoFromUrlFailure",
  QosError_FilePickerBootFailed = "FilePickerBootFailed",
  QosError_RetrievingWopiHostDataFailed = "RetrievingWopiHostDataFailed",
  QosError_SelectedItemNotReturned = "SelectedItemNotReturned",
  QosError_SelectedItemMissingProperties = "SelectedItemMissingProperties",
  QosError_UrlNotProvided = "UrlNotProvided",
  QosError_CallbackNotDefined = "CallbackNotDefined",
  QosError_PrintRedirectFailedToOpenBrowser = "PrintRedirectFailedToOpenBrowser",
  QosError_PrintRedirectUnknownFailure = "PrintRedirectUnknownFailure",
  QosError_PrintRedirectMissingWebUrl = "PrintRedirectMissingWebUrl",
  QosError_PrintRedirectFailedToOpenPdf = "PrintRedirectFailedToOpenPdf",
  QosError_PrintInBrowserFailedToOpenPdf = "PrintInBrowserFailedToOpenPdf",
  QosError_PrintInBrowserUnknownFailure = "PrintInBrowserUnknownFailure",
  QosError_PrintInBrowserWopiFailed = "PrintInBrowserWopiFailed",
}

// Error names more specific than QosErrorName. Logged as extendedProperties in small
// Qos calls to log small QosErrors.
export enum WacErrorName {
  WacError_Bootstrapfile_WacDocumentVroomError = "Bootstrapfile_WacDocumentVroomError",
  WacError_LoadDocument_WacDocumentVroomError = "LoadDocument_WacDocumentVroomError",
  WacError_WacDocumentBootstrapperLoadError = "WacDocumentBootstrapperLoadError",
  WacError_WacInitializeWopiPendingError = "WacInitializeWopiPendingError",
  WacError_WacDocumentGenericBootError = "WacDocumentGenericBootError",
  WacError_WacDocumentJsApiV1Error = "WacDocumentJsApiV1Error",
  WacError_WacDocumentJsApiV2Error = "WacDocumentJsApiV2Error",
  WacError_WacDocumentJsApiV2Error_App = "WacDocumentJsApiV2Error_App",
  WacError_WacDocumentJsApiV2Error_JsApi = "WacDocumentJsApiV2Error_JsApi",
  WacError_WacDocumentJsApiV2Error_Timeout = "WacDocumentJsApiV2Error_Timeout",
  WacError_OwlPrintError = "OwlPrintError",
}

export interface ErrorInfo {
  wacErrorName: WacErrorName;
  qosErrorName: QosErrorName;
  errorMessage: string;
  errorName?: string;
  error?: any;
  httpErrorCode?: any;
  networkError?: boolean;
  inStaging?: boolean;
  documentLoadedWithTabSwitch?: boolean;
}

export enum ExpectedFailureReason {
  PermissionError = "PermissionError",
  NotFound = "NotFound",
  Throttling = "Throttling",
  Offline = "Offline",
  Other = "Other",
}

// this type is used specifically by office.com to provide the office.com specific configurations
// to the lean ui host. Unified Ui Host package doesn't use this type.
export interface LeanUiHostConfig {
  correlationId: string;
  isMsa: boolean;
  puid: string;
  tenantId: string;
  market: string;
  officeHomeApiRoot: string;
  shareApiEndpoint: string;
  organizationName: string;
  leanCapabilitiesMap: any;
}

export interface UnifiedUiHostConfig {
  officeHomeCdnDomain: string;
  shareApiEndpoint: string;
  GraphResourcePath: string;
  unifiedHostCapabilitiesMap: any;
  oneNoteEndPoint: string;
  genericFeatureGates: string;
  deploymentEnvironment: string;
}

// Boot diagnostics for remote meeting sessions
export interface MeetingLiveDiagnostics {
  callId: string;
  shareToStageId?: string;
  isMeetingPresenter?: boolean;
  fileShareFlowDiagnostics?: MeetingLiveFileShareFlowDiagnostics;
}

export interface MeetingLiveFileShareFlowDiagnostics {
  permissionFlowStartTime?: number;
  permissionDialogLoadTime?: number;
  fileShareClickTime?: number;
  fileShareLinkCreateTime?: number;
}

export interface BootDiagnostics {
  hostPageFirstFlushTime?: number;
  tabsCacheHit?: boolean; // if the tab cache of the app is hit.
  packageEntryTime?: number;
  isTestMode?: boolean;
  isManifestCachingEnabled?: boolean;
  isOwlBootstrapperEnabled?: boolean;
  isPrebooted?: boolean;
  isVroom21?: boolean; // if OWL is calling vroom 2.1
  ringId?: string;
  teamsContextStartTime?: number;
  teamsContextEndTime?: number;
  wopiDataStartTime?: number;
  wopiDataEndTime?: number;
  bootStrapStartTime?: number;
  bootStrapEndTime?: number;
  driveItemStartTime?: number;
  driveItemEndTime?: number;
  driveItemFromWacStartTime?: number;
  driveItemFromWacEndTime?: number;
  accessTokenStartTime?: number;
  accessTokenEndTime?: number;
  perfNavigationTiming?: PerformanceNavigationTiming;
  legacyPerformanceData?: LegacyPerformanceData;
  isCookieBlocked?: boolean;
  meetingLiveDiagnostics?: MeetingLiveDiagnostics;
  spRequestGuid?: string;
  createNewStartTime?: number;
  createNewEndTime?: number;
  loadManifestStartTime?: number;
  loadManifestEndTime?: number;
  getManifestFromRemoteTimeElapsed?: number;
  loadManifestServerTimeElapsed?: number;
  loadOwlStartTime?: number;
  loadOwlEndTime?: number;
  owlBootstrapperStartTime?: number;
  owlBootstrapperEndTime?: number;
  isOdcMigrated?: boolean;
  wacTokenFetchStartTime?: number;
  wacTokenFetchEndTime?: number;
  perfOwlTiming?: CustomPerformanceResourceTiming;
  perfJsapiTiming?: CustomPerformanceResourceTiming;
  isMsalV3DownloadEnabled?: boolean;
  owletBootApplicationStartTime?: number;
  owletGetDriveItemStartTime?: number;
  owletGetDriveItemEndTime?: number;
  mdcppLaunchStartTime?: number;
}

export const bootDiagnostics: BootDiagnostics = {};

// UiHostSessionConfig contains settings for configuring the document session. This type is
// used by lean ui host package for booting a document. This type is not exposed directly to the caller
// unified ui host package.
// the data in this type is specific to a document session and might change between sessions
// NOTE: any new setting that will change between sessions should be added into this type
export interface UiHostSessionConfig {
  hostCorrelationId: string; // Ui Host Session Id
  isMsa: boolean; // a flag indicates if the current tenant is a MSA tenant
  tenantId: string; // current tenant id
  shareApiEndpoint: string; // Share service endpoint, used for loading share dialog
  organizationName: string; // organization name of current user
  metaUiHostVersion?: string; // OWL version
  logger?: AdaptiveLogger;
  health?: Health;
  sessionId?: string; // current OWL Session Id. NOTE: OWL session ID = WAC session ID
  isESSEnabled?: boolean; // if Early Session Start is enabled for current session
  experiments?: { [experiment: string]: number }; // experiments enabled on the current session
  highContrastMode?: string; // the high contrast mode applied to the current WAC Session
  additionalApplicationParameters?: { [key: string]: string };
  bootDiagnostics?: BootDiagnostics;
  uiHostTabId?: string;
  upn?: string;
  meeting?: MeetingInfo;
  customAuthConfigValue?: Promise<string> | string;
  wiseEnvironment: WiseEnvironment; // The WISEEnvironment that WISE should send telemetry to
  hostClientType?: string;
  channelId?: string; // id of the channel within a host in which OWL session is started.
  isSynthetic?: boolean; // Whether or not the session is synthetic (not real user traffic, e.g. E2E testing)
  hostUrl?: Promise<string> | string;
  owlVersion?: string;
  owlRollout?: string;
  loginHint?: string;
  isAnonymous?: boolean;
  origin?: string;
  wacToken?: string; // Prefetched wac token from Ui Host
  createNewParams?: OwlCreateNewParams;
  fileImmutableReason?: number; // indicates hints from SPO on various states(including file state)
  supportsZipIt?: boolean; // Whether the file supports ZipIt operations
  previousSession?: string; // For OCDI to feed in and pass to jsAPI further
  previousSessionSrc?: string; // For OCDI to feed in and pass to jsAPI further
}

// UiHostConfig contains settings for configuring behaviors or properties of the ui host
// The ui host settings that don't change between document sessions should go into this type
export interface UiHostConfig {
  hideHeader?: boolean;
  hideAppLauncherInHeader?: boolean;
  hideSearchInHeader?: boolean;
  hideMeInHeader?: boolean;
  hideChatInHeader?: boolean;
  hideWorkflowInHeader?: boolean;
  hideSettingsInHeader?: boolean;
  showAppIconInHeader?: boolean;
  useNeutralColorInHeader?: boolean;
  supportsAuthToken?: boolean;
  authTokenStrategy?: "PairwiseBroker" | "ParentFrame";
  supportsAtMentions?: boolean;
  supportsAccessibilityLoop?: boolean;
  supportsCreateNewDocument?: boolean;
  uiHostName?: string;
  sessionOriginPrefix?: string;
  supportsExcelExternalLinks?: boolean;
  showCloseButton?: boolean;
  openInBrowserEnabled?: boolean;
  supportsExplicitUnload?: boolean;
  supportsMoveToFolder?: boolean;
  supportsShare?: boolean;
  uiHostIntegrationType?: string;
  ellipsisDownloadACopyButtonEnabled?: boolean;
  openLinksInNewWindow?: boolean;
  copyLinkEnabled?: boolean;
  supportsDocumentReboot?: number | DocRebootSupportMode;
  supportsCommonFilePicker?: boolean;
  supportsPopAndPushState?: boolean;
  supportsCustomAuthConfig?: boolean;
  supportsDirectPrint?: boolean;
  supportsDirectDownload?: boolean;
  contentAssemblyMode?:
    | "Templatization"
    | "DocGen"
    | "ContentAssemblyEdit"
    | "ContentAssemblyView";
  uiEmbed?: boolean;
  featureGates?: Map<string, boolean>;
  supportsRefreshAccessToken?: boolean;
  supportsHostUiControls?: boolean;
  supportsRefreshTokenReauthPopUp?: boolean;
  supportsInternalReboot?: boolean;
  conversationButtonEnabled?: boolean;
  disableOfficeAddins?: boolean;
  supportsInPlaceCreateNew?: boolean;
  disableEmbedViewCodeSnippet?: boolean;
  disableCoEditInTeams?: boolean;
  useConsumerFileEmbedHandler?: boolean;
}

export interface BootParams {
  documentBootInfo: DocumentBootInfo;
  fetchItemFromNetwork: GraphItemFetchFunction;
  fetchAccessToken: AccessTokenResponseFunction;
  openShareControl: OpenShareControlFunction;
  sessionConfig: UiHostSessionConfig;
  container: HTMLElement;
  // Probably should be optional because not used in all
  // integrations of bootDocumentWithCustomFunctions (e.g. SPO)
  createDocument: CreateDocumentFunction;
  setTitle?: (title: string) => void;
  onBootComplete?: (
    docInfo: DocumentBootInfo,
    wacBootDiagnostics?: AppInitDiagnostics,
    bootedDocInfo?: DocumentBootInfo
  ) => void;
  onBootFailure?: (errorInfo: ErrorInfo, docInfo?: DocumentBootInfo) => void;
  onReturnFocus?: (navigateForward?: boolean) => void;
  onAcquireOAuthTokenTeams?: (AuthUrl?: string) => void;
  onConversationClose?: () => void;
  onNavigateBack?: () => void;
  onOpenPageLevelConversation?: (
    conversationId: string,
    pageUrl: string,
    hostSendConversationId: (conversationId: string) => void
  ) => void;
  onDocumentClose?: () => void;
  onSuccessNotification?: (useNewEuplReporting?: boolean) => void;
  onDismissHostSpinner?: () => void;
  onOpenInMobileApp?: (openInAppUrl: string, storeUrl: string) => void;
  onUserActivity?: () => void;
  uiHostConfig?: UiHostConfig;
  additionalApplicationParameters?: { [key: string]: string };
  sharingLinkTokenProvider?: SharingLinkTokenProvider;
  // Host-defined event handlers
  onCAAEMessage?: OfficeApplicationEventHandler<OfficeApplicationCAAEMessageEventArgs>;
  onUserAccess?: OfficeApplicationEventHandler<OfficeApplicationUserAccessEventArgs>;
  onMoveToFolder?: OfficeApplicationEventHandler<OfficeApplicationMoveToFolderEventArgs>;
  onSharedFilePicker?: OfficeApplicationEventHandler<OfficeApplicationSharedFilePickerEventArgs>;
  onRename?: (newName: string) => void;
  initPackageParams?: InitPackageParams;
  onFrameChange?: OfficeApplicationEventHandler<OfficeApplicationFrameChangedEventArgs>;
  invokeSessionRefreshInfoPopupBlockedDialog?: (
    props: PopupBlockedDialogProps
  ) => void;
  onFileEmbed?: () => void;
  // Temporary overrides that allow us to flight OWL bundled with JSAPI
  initializeTwoStepCreateNew?: InitializeTwoStepCreateNewFn;
  bootstrapFile?: BootstrapFileFn;
}

export interface UiHostEventHandlers {
  onBootComplete?: (
    docInfo: DocumentBootInfo,
    wacBootDiagnostics?: AppInitDiagnostics
  ) => void;
  onBootFailure?: (errorInfo: ErrorInfo, docInfo?: DocumentBootInfo) => void;
  onReturnFocus?: (navigateForward?: boolean) => void;
  onAcquireOAuthTokenTeams?: (AuthUrl?: string) => void;
  onConversationClose?: () => void;
  onNavigateBack?: () => void;
  onOpenPageLevelConversation?: (
    conversationId: string,
    pageUrl: string,
    hostSendConversationId: (conversationId: string) => void
  ) => void;
  onDocumentClose?: () => void;
  onSuccessNotification?: () => void;
  onDismissHostSpinner?: () => void;
  onOpenInMobileApp?: (openInAppUrl: string, storeUrl: string) => void;
  // Is this ever actually used in the bootDocument flow?
  // It looks like no host ever provides this so maybe it should be removed.
  onRename?: (newName: string) => void;
  // Temporary overrides that allow us to flight OWL bundled with JSAPI
  initializeTwoStepCreateNew?: InitializeTwoStepCreateNewFn;
  bootstrapFile?: BootstrapFileFn;
}

export type BootstrapFileFn = (
  bootstrapParams: WacBootstrapParams,
  uiHostConfig?: UiHostConfig,
  uiHostSessionConfig?: UiHostSessionConfig,
  onBootFailedCallback?: (
    errorInfo: ErrorInfo,
    docInfo?: DocumentBootInfo
  ) => void,
  onSuccessNotification?: () => void,
  registerJsApiV2EventHandlers?: (
    officeApplication: OfficeApplication,
    wopiHostData: WopiHostData,
    documentBootInfo: DocumentBootInfo,
    officeDocumentSession?: OfficeDocumentSession
  ) => void,
  cancellationToken?: CancellationToken,
  jsApiUrl?: string,
  jsApiSri?: string
) => void;

/**
 * Used to pass a sharing link token for shareable address bar link.
 */
export interface SharingLinkTokenProvider {
  TokenPromise: Promise<SharingLinkToken>; // the promise that get back the shareable address bar link token
}

/**
 * Used to pass a sharing link token for shareable address bar link.
 */
export interface SharingLinkToken {
  Key: string; // query parameter key for the token
  Value: string; // query parameter value for the token
}

export enum WdOriginType {
  New = "NEW",
  BackstageMru = "BACKSTAGE-MRU",
  Reboot = "REBOOT",
}

/**
 * Modified version of JSAPI v2's CreateNewParams that only include the types we actually need to
 * provide for OWL (the rest can be inferred from DocumentBootInfo, UiHostSessionConfig, and UiHostConfig)
 */
export type OwlCreateNewParams = Pick<
  CreateNewParams,
  | "appUrl"
  | "file"
  | "customFontCatalogUrl"
  | "contentBundle"
  | "bootParams"
  | "preseededSession"
  | "throttling"
>;
